import { useMemo, useState } from "react";
import type { MastheadProps } from "@uhg-harmony/react";
import { useNotificationsWebSocketConnection } from "../../features/notifications/hooks/useNotificationsWebSocketConnection";
import { useECPNotifications } from "../../features/notifications/hooks/useECPNotifications";
import { NOTIFICATION_FILTERS } from "../../features/notifications/constants";
import type { UserRole } from "../../redux/user/userTypes";

type UseAppNotificationsParams = {
  env: string;
  userRole: UserRole;
  selectedAccountValue?: string;
  token: string;
  isAuthSyncComplete: boolean;
  clientsHasFetched: boolean;
  clientsLoading: boolean;
};

export function useAppNotifications({
  env,
  userRole,
  selectedAccountValue,
  token,
  isAuthSyncComplete,
  clientsHasFetched,
  clientsLoading,
}: UseAppNotificationsParams): MastheadProps["notificationMenuValues"] {
  const [wsReady, setWsReady] = useState(false);

  const subscriptionVariable = useMemo(() => {
    const baseFilters: string[] = [NOTIFICATION_FILTERS.TEMPLATE_FILTERS];
    const selectedAccountFilter = selectedAccountValue?.trim() || "";
    const accountFilters: string[] =
      userRole === "TB_PLATFORM_ADMIN" || userRole === "TB_CLIENT_ADMIN"
        ? selectedAccountFilter
          ? [selectedAccountFilter]
          : []
        : [];

    let filters: string[] = baseFilters;

    if (userRole === "TB_PLATFORM_ADMIN" || userRole === "TB_CLIENT_ADMIN") {
      filters = [...accountFilters];
    } else if (
      userRole === "TB_ECP_ADMIN" ||
      userRole === "TB_GENERAL_READONLY"
    ) {
      filters = baseFilters;
    }

    filters = Array.from(new Set(filters));

    sessionStorage.setItem("notificationFilters", JSON.stringify(filters));

    return {
      recipientIdAttribute: null,
      recipientIdpAttribute: null,
      filters,
    };
  }, [userRole, selectedAccountValue]);

  const requiresAccountScopedConnection =
    userRole === "TB_PLATFORM_ADMIN" || userRole === "TB_CLIENT_ADMIN";
  const hasResolvedAccountForConnection =
    !requiresAccountScopedConnection || !!selectedAccountValue;

  const isWebSocketReady =
    isAuthSyncComplete &&
    !!token &&
    clientsHasFetched &&
    !clientsLoading &&
    !!userRole &&
    (subscriptionVariable.filters?.length ?? 0) > 0 &&
    hasResolvedAccountForConnection;

  useNotificationsWebSocketConnection({
    env,
    subscriptionVariable,
    setWsReady,
    enabled: isWebSocketReady,
  });

  const ecpNotifications = useECPNotifications({
    env,
    wsReady,
    initialDays: 30,
  });

  return ecpNotifications as unknown as MastheadProps["notificationMenuValues"];
}
